import CheckIcon from '@mui/icons-material/Check';
import {
    Box,
    Button,
    Chip,
    Icon,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography
} from '@mui/material';
import { type SortDescriptor } from '@progress/kendo-data-query';
import { memo, useMemo, type ReactNode } from 'react';
import { dashboardCategoryIcons } from './dashboard-category-icons';
import './Dashboard.css';
import {
    DashboardCustomColumn,
    DashboardFlightColumn,
    DashboardFlightRow,
    DashboardFlightsPanelProps,
    DashboardFlightsPanelVariant
} from './DashboardFlightsPanel';

const EMPTY_CUSTOM_COLUMNS: DashboardCustomColumn[] = [];

type PanelSnapshot = { data: DashboardFlightRow[]; total: number; error: boolean };

function getColumnValue(column: DashboardFlightColumn | DashboardCustomColumn, row: DashboardFlightRow): unknown {
    return 'tagSelector' in column ? (row[column.tagSelector] ?? row[column.field ?? '']) : (row[column.id] ?? row[column.field ?? '']);
}

function renderValue(column: DashboardFlightColumn | DashboardCustomColumn, row: DashboardFlightRow): ReactNode {
    const value = getColumnValue(column, row);
    return value == null ? '' : String(value);
}

type ViewProps = Pick<DashboardFlightsPanelProps, 'title' | 'columns' | 'customColumns' | 'emptyText' | 'getCellStyle'> & {
    variant: DashboardFlightsPanelVariant;
    snapshot: PanelSnapshot;
    sort: SortDescriptor[];
    onSortChange: (field: string) => void;
    onRetry: () => void;
};

const DashboardFlightsTable = memo(function DashboardFlightsTable({
    title,
    columns,
    customColumns,
    emptyText,
    variant,
    getCellStyle,
    snapshot,
    sort,
    onSortChange,
    onRetry
}: ViewProps) {
    const { data, total, error } = snapshot;
    const visibleColumns = useMemo(
        () => [...columns, ...(customColumns ?? EMPTY_CUSTOM_COLUMNS)].filter((column) => !column.hidden),
        [columns, customColumns]
    );
    const activeSort = sort[0];

    return (
        <Paper component="section" elevation={0} className={`dashboard-card dashboard-flight-panel dashboard-flight-panel--${variant}`}>
            <Box component="header" className="dashboard-flight-panel__header">
                <Box className="dashboard-flight-panel__title">
                    <Box component="span" className="dashboard-summary__icon" aria-hidden="true">
                        <Icon component={dashboardCategoryIcons[variant]} />
                    </Box>
                    <Typography
                        component="h2"
                        className="dashboard-flight-panel__title-text"
                        sx={{ m: 0, fontSize: '0.84rem', fontWeight: 650, lineHeight: 1.3 }}>
                        {title}
                    </Typography>
                    <Chip
                        size="small"
                        label={total.toLocaleString()}
                        className="dashboard-flight-panel__count"
                        aria-label={`${total} flights`}
                        sx={{ fontSize: '0.72rem', fontWeight: 700, height: 25, flexShrink: 0 }}
                    />
                </Box>
            </Box>
            <Box className="dashboard-flight-panel__content">
                <TableContainer
                    className={`dashboard-flights-table-container${data.length === 0 ? ' dashboard-flights-table-container--empty' : ''}`}>
                    <Table stickyHeader className="dashboard-flights-table" aria-label={title}>
                        <TableHead>
                            <TableRow>
                                {visibleColumns.map((column, index) => {
                                    const field = 'tagSelector' in column ? column.field : column.id;
                                    const active = Boolean(
                                        field && activeSort && (activeSort.field === field || activeSort.field === column.field)
                                    );
                                    const direction = active && activeSort?.dir === 'desc' ? 'desc' : 'asc';
                                    const label = column.title ?? column.field ?? '';
                                    return (
                                        <TableCell
                                            key={column.id ?? column.field ?? index}
                                            scope="col"
                                            align={column.justify ?? 'left'}
                                            className={column.headerClassName}
                                            sortDirection={active ? direction : false}>
                                            {column.sortable === false || !field ? (
                                                label
                                            ) : (
                                                <TableSortLabel
                                                    active={active}
                                                    direction={direction}
                                                    hideSortIcon
                                                    onClick={() => {
                                                        if (field) onSortChange(field);
                                                    }}
                                                    aria-label={`${label}, sort ${active && direction === 'asc' ? 'descending' : 'ascending'}`}>
                                                    <span className="dashboard-flights-table-heading">{label}</span>
                                                </TableSortLabel>
                                            )}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.map((row) => (
                                <TableRow key={row.id}>
                                    {visibleColumns.map((column, index) => (
                                        <TableCell
                                            key={column.id ?? column.field ?? index}
                                            align={column.justify ?? 'left'}
                                            className={column.className}
                                            style={getCellStyle?.(column, getColumnValue(column, row), row)}>
                                            {column.renderCell
                                                ? column.renderCell(getColumnValue(column, row), row)
                                                : renderValue(column, row)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {data.length === 0 && (
                        <Box className="dashboard-flights-table-empty" role={error ? 'alert' : 'status'}>
                            {error ? (
                                <>
                                    <span>Unable to load flights.</span>
                                    <Button size="small" onClick={onRetry}>
                                        Retry
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Box className="dashboard-flight-grid__empty-icon" aria-hidden="true">
                                        <Icon component={CheckIcon} />
                                    </Box>
                                    <span>{emptyText}</span>
                                </>
                            )}
                        </Box>
                    )}
                </TableContainer>
            </Box>
        </Paper>
    );
});

export default DashboardFlightsTable;
